/*
 * Copyright (c) 2008-2022 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

import api from "@/config/api"
import Vue from "vue"
import { LOAD_STATES, PagedRequestHandler } from "../utils/PagedRequestHandler"
import { EventBus } from "~/common/src/event-bus"
import { TENANTS_ASSIGNED_TENANTS_UPDATED } from "../config/events"

const tenantRequestHandler = Vue.observable(new PagedRequestHandler(api.TENANTS))

const requestFilter = function (sortOption) {
  let finalObject = {}

  finalObject["sort"] = sortOption.urlValue + (sortOption.asc ? ",asc" : ",desc")

  return finalObject
}

/**
 * Remove tenants from tenantMap that are neither referenced
 * from tenantList nor assignedTenants nor tenantFilterId
 *
 * @param {*} state
 */
function removeUnusedTenants(state) {
  Object.keys(state.tenantMap).forEach((id) => {
    if (!state.tenantList.includes(id) && !state.assignedTenants.includes(id) && state.tenantFilterId !== id)
      delete state.tenantMap[id]
  })
}

EventBus.$on("OAuth:logout", () => {
  window.localStorage.removeItem("viewAsTenant")
})

export default {
  namespaced: true,

  state: {
    tenantMap: {}, // map of tenantIds to tenant objects
    tenantList: [], // list of tenantIds in the order they are displayed on the tenant admin page
    assignedTenants: [], // list of tenantIds that are assigned to objects in other store modules (e.g. meetings and users)
    tenantFilterId: null,
    searchQuery: "",
    sortOptions: {
      active: 0,
      options: [
        {
          text: "ascendingName",
          urlValue: "name",
          asc: true,
        },
        {
          text: "descendingName",
          urlValue: "name",
          asc: false,
        },
        {
          text: "ascendingContactMail",
          urlValue: "contactMail",
          asc: true,
        },
        {
          text: "descendingContactMail",
          urlValue: "contactMail",
          asc: false,
        },
        {
          text: "ascendingModificationDate",
          urlValue: "lastModifiedDate",
          asc: true,
        },
        {
          text: "descendingModificationDate",
          urlValue: "lastModifiedDate",
          asc: false,
        },
        {
          text: "ascendingCreationDate",
          urlValue: "createdDate",
          asc: true,
        },
        {
          text: "descendingCreationDate",
          urlValue: "createdDate",
          asc: false,
        },
      ],
    },
  },

  mutations: {
    /**
     * Store provided tenants to state. Purges old state data if flag is set
     *
     * @param {Object} {
     *            tenants - list of tenants to store
     *            purge - flag indicating if current data should be removed
     *             }
     */
    setTenants(state, { tenants, purge }) {
      if (purge) {
        state.tenantList = []
      }

      tenants.forEach((t) => {
        Vue.set(state.tenantMap, t["id"], t)

        if (!state.tenantList.includes(t["id"])) state.tenantList.push(t["id"])
      })

      removeUnusedTenants(state)
    },

    /**
     * Store provided assigned tenants to state.
     *
     * @param {Object} {
     *              tenants - list of tenants to store
     *             }
     * @emits {@link TENANTS_ASSIGNED_TENANTS_UPDATED} event
     */
    setAssignedTenants(state, { tenants }) {
      state.assignedTenants = []

      tenants.forEach((t) => {
        Vue.set(state.tenantMap, t["id"], t)
        state.assignedTenants.push(t["id"])
      })

      removeUnusedTenants(state)
      EventBus.$emit(TENANTS_ASSIGNED_TENANTS_UPDATED)
    },

    /**
     * commit new search query value
     *
     * @param state
     * @param search
     */
    setSearchQuery(state, search) {
      state.searchQuery = search
    },

    /**
     * Set last active sort
     *
     * @param {*} state
     * @param {*} sortVal
     */
    setLastActiveSort(state, sortVal) {
      state.sortOptions.active = sortVal
    },

    /**
     * Set tenant to manage associated meetings and users
     * @param {*} state
     * @param {string} tenantId
     * @param {Boolean} saveInWebStorage
     * @param {Boolean} loadFromWebStorage
     */
    setTenantFilterId(state, { tenantId, saveInWebStorage = true, loadFromWebStorage = false }) {
      state.tenantFilterId = loadFromWebStorage ? localStorage.getItem("viewAsTenant") : tenantId

      log.debug("tenantFilterId:", state.tenantFilterId)

      if (saveInWebStorage) {
        if (state.tenantFilterId !== null) localStorage.setItem("viewAsTenant", state.tenantFilterId)
        else localStorage.removeItem("viewAsTenant")
      }
    },

    /**
     * Reset search and sorting and set tenant load state back to UNLOADED
     * @param {*} state
     */
    resetTenantState(state) {
      state.searchQuery = ""
      state.sortOptions.active = 0
      tenantRequestHandler.setCurrentPage(0)
    },
  },

  actions: {
    /**
     * Perform the request for tenants from server.
     *
     * @param {Object} {
     *    purge
     *    }
     *  @returns Promise
     */
    loadTenants({ commit }, { purge }) {
      return new Promise((resolve, reject) => {
        if (tenantRequestHandler.LoadStatus === LOAD_STATES.LOADING) return

        if (purge) tenantRequestHandler.setCurrentPage(0)
        tenantRequestHandler
          .retrieveNextPage()
          .then(
            (result) => {
              commit("setTenants", {
                tenants: result,
                purge: purge,
              })
              resolve(result)
            },
            (error) => {
              reject(error)
            }
          )
          .catch((error) => log.warn(error))
      })
    },

    /**
     * Perform request to retrieve the tenants that are assigned to meetings and users.
     *
     * Note: For tenant-users, only the own tenant is retrieved.
     */
    async loadAssignedTenants({ commit, getters, rootGetters }) {
      // Tenant-users can only access information for their own tenant
      let authenticatedUser = await rootGetters["user/userSettingsPromise"]
      if (authenticatedUser?.tenantId !== null) {
        // avoid loading more than once
        if (getters["getAssignedTenantIds"].length > 0) {
          return
        }
        Vue.http.get(api.ME_TENANT()).then(
          (response) => {
            commit("setAssignedTenants", { tenants: [response.body] })
          },
          (response) => {
            log.error(
              "Could not load assigned tenants, error: ",
              response.status,
              "/ error message:",
              response.statusText
            )
          }
        )
        return
      }
      // Global users without viewTenant permissions are currently not supported
      else if (!rootGetters["auth/hasPermission"]("viewTenant")) {
        log.warn("Unsupported condition: Global user without viewTenant permission")
        return
      }

      const userTenants = rootGetters["users/list"]
        .filter((user) => user.tenantId != undefined)
        .map((user) => user.tenantId)
      const meetingTenants = rootGetters["meetings/allMeetings"].map((meeting) => meeting.tenantId)
      const tenantFilterId = getters["hasTenantFilter"] ? getters["getTenantFilterId"] : []

      const uniqueTenants = [...new Set([...userTenants, ...meetingTenants, tenantFilterId])]
      if (uniqueTenants.length === 0) return
      Vue.http.get(api.TENANTS_BY_IDS(uniqueTenants)).then(
        (response) => {
          commit("setAssignedTenants", {
            tenants: response.body.content,
          })
        },
        (response) => {
          log.error(
            "Could not load assignment tenants, error: ",
            response.status,
            "/ error message:",
            response.statusText
          )
        }
      )
    },

    /**
     * Perform request to create a new tenant on server and commits returned tenant on success
     *
     * @param {Object} tenant
     * @returns Promise
     */
    createTenant({ commit }, tenant) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.TENANTS(), tenant).then(
          (resp) => {
            tenantRequestHandler.retrieveToCurrentPage().then((result) => {
              commit("setTenants", {
                tenants: result,
                purge: true,
              })
            })
            resolve(resp.body)
          },
          (error) => {
            reject(error)
          }
        )
      })
    },

    /**
     * Perform request to update existing tenant on server
     *
     * @param {Object} tenant
     * @returns Promise
     */
    updateTenant({ commit }, tenant) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.TENANTS(tenant.id), tenant).then(
          (resp) => {
            tenantRequestHandler.retrieveToCurrentPage().then((result) => {
              commit("setTenants", {
                tenants: result,
                purge: true,
              })
            })
            resolve(resp.body)
          },
          (error) => {
            reject(error)
          }
        )
      })
    },

    /**
     * Perform request to delete tenant from server.
     *
     * @param {String} tenantId
     * @returns Promise
     */
    deleteTenant({ commit, dispatch }, tenantId) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.TENANTS(tenantId)).then(
          () => {
            tenantRequestHandler.retrieveToCurrentPage().then((result) => {
              commit("setTenants", {
                tenants: result,
                purge: true,
              })
              dispatch("users/loadUsers", { purge: true }, { root: true })
              dispatch("meetings/loadMeetings", { purge: true }, { root: true })
            })
            resolve(tenantId)
          },
          (error) => {
            reject(error)
          }
        )
      })
    },

    /**
     * Set tenant to manage associated meetings and users
     * @param {*} state
     * @param {String} tenantId
     */
    setTenantFilterId({ commit, dispatch }, tenantId) {
      commit("setTenantFilterId", { tenantId })

      dispatch("users/updateHandlerAndLoadUsers", null, { root: true })
      dispatch("meetings/updateHandlerAndLoadMeetings", null, { root: true })
    },

    /**
     * Update filters and search string in tenant request handler
     * and load fresh page afterwards
     *
     * @param state
     * @param dispatch
     */
    updateHandlerAndLoadTenants({ state, dispatch }) {
      tenantRequestHandler.setFilter(requestFilter(state.sortOptions.options[state.sortOptions.active]))
      tenantRequestHandler.setSearchString(state.searchQuery)
      dispatch("loadTenants", { purge: true })
    },

    /**
     * Update last active sort and trigger handler update + tenant reload
     *
     * @param commit
     * @param dispatch
     * @param sortVal
     */
    setLastActiveSort({ commit, dispatch }, sortVal) {
      commit("setLastActiveSort", sortVal)
      dispatch("updateHandlerAndLoadTenants")
    },

    /**
     * Update search query and trigger handler update + tenant reload
     *
     * @param commit
     * @param dispatch
     * @param search
     */
    setSearchQuery({ commit, dispatch }, search) {
      commit("setSearchQuery", search)
      dispatch("updateHandlerAndLoadTenants")
    },
  },

  getters: {
    /**
     * Returns a list of tenants for the tenant list.
     *
     * Note: This excludes the tenants loaded solely because of their association to an object in state (see
     * {@link getAssignedTenantIds}).
     *
     * @param {*} state
     * @returns Array
     */
    getTenants: (state) => state.tenantList.map((id) => state.tenantMap[id]),

    /**
     * Returns the IDs of all tenants that are assigned to objects currently held in state (meetings, users, etc.).
     *
     * Note: This excludes the tenants loaded solely for the tenant list (see {@link getTenants}).
     *
     * @param state
     * @returns {[]}
     */
    getAssignedTenantIds: (state) => state.assignedTenants,

    /**
     * Return current load state of activities on store.
     *
     * @returns String
     */
    getTenantLoadState: () => tenantRequestHandler.LoadStatus,

    /**
     * Returns the tenant with the given id, if existing. If not exists, return null
     * @param {*} state
     * @param {String} id
     * @returns Object | null
     */
    getTenantById: (state) => (id) => state.tenantMap[id] !== undefined ? state.tenantMap[id] : null,

    /**
     * get sort options
     * @param state
     * @returns {*}
     */
    sortOptions: (state) => state.sortOptions.options,

    /**
     * get last sort setting
     * @param {*} state
     * @returns Number
     */
    lastActiveSort: (state) => state.sortOptions.active,

    /**
     * Returns a boolean indicating if another page of tenants can be retrieved
     * @returns Boolean
     */
    canRetrieveMoreTenants: () => tenantRequestHandler.CurrentPage < tenantRequestHandler.TotalPages - 1,

    /**
     * Returns a boolean indicating if a tenant filter has been applied
     * @param {*} state
     * @returns Boolean
     */
    hasTenantFilter: (state) => state.tenantFilterId !== null,

    /**
     * Returns tenant filter ID or null if not set
     * @param {*} state
     * @returns String | null
     */
    getTenantFilterId: (state) => state.tenantFilterId,

    /**
     * Returns tenant filter or null if not set
     * @param {*} state
     * @returns Object | null
     */
    getTenantFilter: (state) => (state.tenantFilterId !== null ? state.tenantMap[state.tenantFilterId] : null),
  },
}
